import {
    Autocomplete, Box, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, MenuItem, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';

import { DataAPIContext } from '../../api';
import { CREATE, GET_LIST, GET_ONE, UPDATE } from '../../api/dataProvider';
import FormActionButtons from '../../components/FormActionButtons';
import TabPanel, { a11yProps } from '../../components/TabPanel';
import { ContextStore } from '../../store/contextStore';
import BlacklistHistory from './BlacklistHistory';
import ConditionsField, {
    conditionError, conditionFromApi, conditionToApi, EMPTY_CONDITIONS, emptyConditions,
} from './ConditionsField';
import { ACTIONS, DELIVERY_TYPES, MAX_CC_INBOXES, MAX_REDIRECT_INBOXES } from './constants';
import { actionError, actionPayload } from './ruleAction';

const EMPTY = {
    action: 'block',
    cc_email: '',
    delivery_type: 'email',
    is_active: true,
    block_all_events: true,
    event_codes: [],
    event_groups: [],
    exception_event_codes: [],
    exception_event_groups: [],
    comment: '',
    redirect_to_email: '',
    redirect_reply_to_email: '',
    redirect_append_conversation_sid: true,
    conditions: EMPTY_CONDITIONS,
};

/** The wire form of the whole condition list, for the changed-at-all check. */
const conditionsWire = (value) => JSON.stringify({
    mode: (value || EMPTY_CONDITIONS).mode,
    conditions: ((value || EMPTY_CONDITIONS).conditions || []).map(conditionToApi),
});

// Editor dialog for a tenant blacklist rule. Two tabs: the editor and the change
// history. A non-empty "reason for this change" is mandatory before create /
// save (stored in the change history); deletion is handled from the list.
const BlacklistEditDialog = ({ open, ruleId, onClose, onSaved }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const isEdit = ruleId != null;

    const [tab, setTab] = React.useState(0);
    const [form, setForm] = React.useState(EMPTY);
    const [reason, setReason] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [groups, setGroups] = React.useState([]);
    const [codes, setCodes] = React.useState([]);
    const [otas, setOtas] = React.useState([]);
    const [resellers, setResellers] = React.useState([]);
    const [tagRegistry, setTagRegistry] = React.useState(null);
    // wire snapshot of the condition list as loaded; null = new rule
    const initialConditionsRef = React.useRef(null);

    const canWrite = hasPerm('blacklist_rule', 'write');
    const canHistory = hasPerm('blacklist_rule', 'history');

    // reset on open
    React.useEffect(() => {
        if (!open) return;
        setTab(0);
        setReason('');
        if (!isEdit) {
            setForm({ ...EMPTY, conditions: emptyConditions() });
            initialConditionsRef.current = null;
        }
    }, [open, isEdit]);

    // load reference data (event groups + codes) once when the dialog opens
    React.useEffect(() => {
        if (!open) return;
        dataAPI(GET_LIST, 'blacklist_rule', { urlSuffix: 'event_groups/', pagination: { page: 1, perPage: 500 }, filter: {} })
            .then((res) => setGroups(res.data || []))
            .catch(() => setGroups([]));
        dataAPI(GET_LIST, 'blacklist_rule', { urlSuffix: 'event_codes/', pagination: { page: 1, perPage: 1000 }, filter: {} })
            .then((res) => setCodes(res.data || []))
            .catch(() => setCodes([]));
        dataAPI(GET_LIST, 'blacklist_rule', { urlSuffix: 'otas/', pagination: { page: 1, perPage: 100 }, filter: {} })
            .then((res) => setOtas(res.data || []))
            .catch(() => setOtas([]));
        dataAPI(GET_LIST, 'blacklist_rule', { urlSuffix: 'reseller_tags/', pagination: { page: 1, perPage: 100 }, filter: {} })
            .then((res) => setResellers(res.data || []))
            .catch(() => setResellers([]));
        // registries for the tag-condition builder (sources + match modes)
        dataAPI(GET_ONE, 'blacklist_rule', { urlSuffix: 'tag_sources/' })
            .then((res) => setTagRegistry(res.data || null))
            .catch(() => setTagRegistry(null));
    }, [open, dataAPI]);

    // load the full record when editing
    React.useEffect(() => {
        if (!open || !isEdit) return;
        dataAPI(GET_ONE, 'blacklist_rule', { id: ruleId })
            .then((res) => {
                const d = res.data;
                const loadedConditions = {
                    mode: d.condition_match_mode || 'all',
                    // `conditions` always carries condition #1 first; older
                    // backends only sent the top-level fields, so fall back to
                    // rebuilding a one-condition list out of those.
                    conditions: (d.conditions && d.conditions.length > 0
                        ? d.conditions
                        : [d]).map(conditionFromApi),
                };
                initialConditionsRef.current = conditionsWire(loadedConditions);
                setForm({
                    action: d.action || 'block',
                    cc_email: d.cc_email || '',
                    delivery_type: d.delivery_type,
                    is_active: d.is_active,
                    block_all_events: d.block_all_events,
                    event_codes: d.event_codes || [],
                    event_groups: (d.event_groups || []).map((g) => g.id),
                    exception_event_codes: d.exception_event_codes || [],
                    exception_event_groups: (d.exception_event_groups || []).map((g) => g.id),
                    comment: d.comment || '',
                    redirect_to_email: d.redirect_to_email || '',
                    redirect_reply_to_email: d.redirect_reply_to_email || '',
                    redirect_append_conversation_sid: d.redirect_append_conversation_sid !== false,
                    conditions: loadedConditions,
                });
            })
            .catch(() => enqueueSnackbar('Failed to load rule', { variant: 'error' }));
    }, [open, isEdit, ruleId, dataAPI, enqueueSnackbar]);

    const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));

    const conditions = React.useMemo(
        () => (form.conditions || EMPTY_CONDITIONS).conditions || [],
        [form.conditions],
    );

    // A Cc rule (CP-8482) never blocks, so the block-worded labels would mislead.
    const isCc = form.action === 'cc';
    const allEventsLabel = isCc ? 'Apply to all events' : 'Block all events';

    const validationError = React.useMemo(() => {
        if (!reason.trim()) return 'A reason is required.';
        if (conditions.length === 0) return 'A rule needs at least one condition.';
        for (let i = 0; i < conditions.length; i += 1) {
            const err = conditionError(conditions[i]);
            if (err) return conditions.length > 1 ? `Condition ${i + 1}: ${err}` : err;
        }
        if (!form.block_all_events && form.event_codes.length === 0 && form.event_groups.length === 0) {
            return `Select event codes or groups, or enable "${allEventsLabel}".`;
        }
        return actionError(form);
    }, [reason, conditions, form, allEventsLabel]);

    const handleSave = () => {
        if (validationError) {
            enqueueSnackbar(validationError, { variant: 'warning' });
            return;
        }
        // One ordered list; the backend writes #1 onto the rule and the rest as
        // child rows. Never send the legacy top-level condition keys alongside it
        // -- that combination is rejected. On edit the list is REPLACE-style, so
        // it is omitted entirely when untouched: an unrelated tweak (comment,
        // active toggle) must not rewrite the condition rows from this dialog's
        // possibly stale snapshot, churn their ids, or clobber a condition someone
        // added elsewhere meanwhile.
        const wire = conditionsWire(form.conditions);
        const conditionsChanged = !isEdit || initialConditionsRef.current === null
            || wire !== initialConditionsRef.current;
        const payload = {
            delivery_type: form.delivery_type,
            ...(conditionsChanged ? {
                condition_match_mode: (form.conditions || EMPTY_CONDITIONS).mode,
                conditions: conditions.map(conditionToApi),
            } : {}),
            is_active: form.is_active,
            block_all_events: form.block_all_events,
            event_codes: form.block_all_events ? [] : form.event_codes,
            event_group_ids: form.block_all_events ? [] : form.event_groups,
            exception_event_codes: form.exception_event_codes,
            exception_event_group_ids: form.exception_event_groups,
            comment: form.comment.trim() || null,
            ...actionPayload(form),
            reason: reason.trim(),
        };
        setSaving(true);
        const call = isEdit
            ? dataAPI(UPDATE, 'blacklist_rule', { id: ruleId, data: payload })
            : dataAPI(CREATE, 'blacklist_rule', { data: payload });
        call
            .then((res) => {
                if (res && res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.body?.message || 'Save failed', { variant: 'error' });
                    return;
                }
                if (isCc && res?.data && res.data.action !== 'cc') {
                    // A backend from before CP-8482 ignores `action` and stores a BLOCK rule,
                    // which would stop these guest emails instead of copying them.
                    enqueueSnackbar('This server does not support Cc rules yet, so the rule was saved as a'
                        + ' blocking rule. Deactivate or delete it.', { variant: 'error', persist: true });
                    onSaved && onSaved();
                    return;
                }
                enqueueSnackbar(isEdit ? 'Rule updated' : 'Rule created', { variant: 'success' });
                onSaved && onSaved();
                onClose && onClose();
            })
            .catch((e) => enqueueSnackbar(e?.body?.message || 'Save failed', { variant: 'error' }))
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit blacklist rule' : 'Create New Blacklist Rule'}</DialogTitle>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3 }} indicatorColor="primary" textColor="primary">
                <Tab label="Editor" {...a11yProps(0)} />
                {isEdit && canHistory && <Tab label="Change history" {...a11yProps(1)} />}
            </Tabs>
            <DialogContent dividers>
                <TabPanel value={tab} index={0}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField select label="Delivery type" value={form.delivery_type}
                            onChange={(e) => set('delivery_type')(e.target.value)} fullWidth>
                            {DELIVERY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </TextField>

                        <TextField select label="When the rule matches" value={form.action}
                            onChange={(e) => set('action')(e.target.value)} fullWidth
                            error={isCc && form.delivery_type !== 'email'}
                            helperText={isCc
                                ? 'The message is delivered as usual, with the addresses below Cc\'d (email only).'
                                : 'The delivery is blocked (and, with redirect inboxes set, sent to them instead).'}>
                            {ACTIONS.map((a) => (
                                <MenuItem key={a.value} value={a.value}
                                    disabled={a.value === 'cc' && form.delivery_type !== 'email'}>
                                    {a.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <ConditionsField
                            value={form.conditions || EMPTY_CONDITIONS}
                            onChange={set('conditions')}
                            otas={otas}
                            resellers={resellers}
                            tagRegistry={tagRegistry}
                        />

                        <Typography variant="caption" color="text.secondary">
                            No rule applies to a delivery method that sends to an arbitrary address,
                            number or thread of its own — those are internal test and monitoring
                            targets, and are never blocked, redirected or Cc'd.
                        </Typography>

                        <FormControlLabel control={<Switch checked={form.is_active}
                            onChange={(e) => set('is_active')(e.target.checked)} />} label="Active" />

                        <FormControlLabel control={<Switch checked={form.block_all_events}
                            onChange={(e) => set('block_all_events')(e.target.checked)} />} label={allEventsLabel} />

                        {!form.block_all_events && (
                            <>
                                <Autocomplete multiple options={groups} value={groups.filter((g) => form.event_groups.includes(g.id))}
                                    getOptionLabel={(g) => g.name}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    onChange={(_, val) => set('event_groups')(val.map((g) => g.id))}
                                    renderInput={(params) => <TextField {...params} label="Event groups" placeholder="Add group" />} />

                                <Autocomplete multiple freeSolo options={codes} value={form.event_codes}
                                    onChange={(_, val) => set('event_codes')(val)}
                                    renderInput={(params) => <TextField {...params} label="Event codes" placeholder="Add event code" />} />
                            </>
                        )}

                        <Divider textAlign="left" sx={{ mt: 1 }}>
                            {isCc ? 'Exceptions (never Cc\'d)' : 'Exceptions (never blocked)'}
                        </Divider>
                        <Autocomplete multiple options={groups} value={groups.filter((g) => form.exception_event_groups.includes(g.id))}
                            getOptionLabel={(g) => g.name}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            onChange={(_, val) => set('exception_event_groups')(val.map((g) => g.id))}
                            renderInput={(params) => <TextField {...params} label="Exception event groups" placeholder="Add group" />} />

                        <Autocomplete multiple freeSolo options={codes} value={form.exception_event_codes}
                            onChange={(_, val) => set('exception_event_codes')(val)}
                            renderInput={(params) => <TextField {...params} label="Exception event codes" placeholder="Add event code"
                                helperText={isCc
                                    ? 'Events matching these are never Cc\'d'
                                    : 'Events matching these are exempted even when blocked above'} />} />

                        <TextField label={isCc ? 'Comment (optional)' : 'Comment (why blocking — optional)'}
                            value={form.comment}
                            onChange={(e) => set('comment')(e.target.value)} fullWidth multiline
                            helperText={isCc ? 'Why these addresses are Cc\'d' : 'Shown in delivery logs when a delivery is blocked'} />

                        {isCc ? (
                            <>
                                <Divider textAlign="left" sx={{ mt: 1 }}>Cc</Divider>
                                <TextField label="Cc these addresses" value={form.cc_email} required
                                    onChange={(e) => set('cc_email')(e.target.value)} fullWidth
                                    error={form.cc_email.split(',').filter((a) => a.trim()).length > MAX_CC_INBOXES}
                                    helperText={`Up to ${MAX_CC_INBOXES} email addresses (comma-separated), Cc'd on every matching email to a guest. The guest still receives it; staff notifications are never Cc'd.`} />
                            </>
                        ) : (
                            <>
                                <Divider textAlign="left" sx={{ mt: 1 }}>Redirect (optional)</Divider>
                                <TextField label="Redirect blocked deliveries to" value={form.redirect_to_email}
                                    onChange={(e) => set('redirect_to_email')(e.target.value)} fullWidth
                                    error={form.redirect_to_email.split(',').filter((a) => a.trim()).length > MAX_REDIRECT_INBOXES}
                                    helperText={`Up to ${MAX_REDIRECT_INBOXES} email inboxes (comma-separated) that receive a copy of every delivery blocked by this rule (instead of the message being dropped silently)`} />
                                {!!form.redirect_to_email.trim() && (
                                    <TextField label="Custom Reply-To email (optional)" value={form.redirect_reply_to_email}
                                        onChange={(e) => set('redirect_reply_to_email')(e.target.value)} fullWidth
                                        error={form.redirect_reply_to_email.includes(',')}
                                        helperText="Replies to the redirected email go to this address (e.g. your main inbox) instead of into the guest's chat conversation" />
                                )}
                                {!!form.redirect_to_email.trim() && !form.redirect_reply_to_email.trim() && (
                                    <FormControlLabel control={<Switch checked={form.redirect_append_conversation_sid}
                                        onChange={(e) => set('redirect_append_conversation_sid')(e.target.checked)} />}
                                        label="Allow the inbox to reply into the guest's conversation (reply appears as the guest)" />
                                )}
                            </>
                        )}

                        <TextField label="Reason for this change (required)" value={reason}
                            onChange={(e) => setReason(e.target.value)} fullWidth required
                            error={!reason.trim()}
                            helperText="Stored in the change history" />
                    </Box>
                </TabPanel>
                {isEdit && canHistory && (
                    <TabPanel value={tab} index={1}>
                        <BlacklistHistory ruleId={ruleId} active={tab === 1} />
                    </TabPanel>
                )}
            </DialogContent>
            <DialogActions>
                <FormActionButtons
                    mode={isEdit ? 'edit' : 'create'}
                    size="medium"
                    onSubmit={handleSave}
                    submitDisabled={saving || !canWrite || !!validationError}
                    onCancel={onClose}
                />
            </DialogActions>
        </Dialog>
    );
};

export default BlacklistEditDialog;
